import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Trash2 } from "lucide-react";

const tabs = [
  { key: "orders", label: "Orders", endpoint: "/admin/orders", path: "/admin/orders" },
  { key: "classes", label: "Class Registrations", endpoint: "/admin/class-registrations", path: "/admin/class-registrations" },
  { key: "subscriptions", label: "Subscriptions", endpoint: "/admin/subscription-signups", path: "/admin/subscription-signups" },
  { key: "events", label: "Event Bookings", endpoint: "/admin/event-bookings", path: "/admin/event-bookings" },
];

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-blue-100 text-blue-800",
  complete: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-gray-200 text-gray-600",
};

export default function AdminOrders() {
  const [tab, setTab] = useState("orders");
  const [data, setData] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const activeTab = tabs.find((t) => t.key === tab);
  const load = () => api.get(activeTab.endpoint).then((r) => setData(r.data));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const markComplete = async (row) => {
    setBusyId(row.id);
    try {
      await api.put(`${activeTab.path}/${row.id}/status`, null, { params: { status_str: "complete" } });
      toast.success("Marked as complete");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this record permanently? This cannot be undone.")) return;
    setBusyId(row.id);
    try {
      await api.delete(`${activeTab.path}/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-3xl text-[color:var(--color-primary)] mb-6">Orders & Bookings</h1>
      <div className="flex gap-2 mb-6 border-b border-[color:var(--color-border)]">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm uppercase tracking-widest ${tab === t.key ? "border-b-2 border-[color:var(--color-accent)] text-[color:var(--color-primary)]" : "text-gray-500"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[color:var(--color-border)] rounded overflow-hidden">
        {data.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No records yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
              <tr>
                <th className="text-left p-3">Name / Item</th>
                <th className="text-left p-3">Contact</th>
                <th className="text-left p-3">Details</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => {
                const status = (r.status || "pending").toLowerCase();
                const isComplete = status === "complete";
                const busy = busyId === r.id;
                return (
                  <tr key={r.id || i} className="border-t border-[color:var(--color-border)]" data-testid={`row-${r.id}`}>
                    <td className="p-3 font-medium">{r.product_name || r.class_title || r.plan_name || r.name}</td>
                    <td className="p-3">
                      <div>{r.customer_name || r.name}</div>
                      <div className="text-xs text-gray-500">{r.customer_email || r.email} · {r.customer_phone || r.phone}</div>
                    </td>
                    <td className="p-3 text-xs text-gray-600">
                      {tab === "orders" && <>Qty {r.quantity} · £{r.price?.toFixed(2)} · {r.delivery_address}{r.variant_name ? ` · ${r.variant_name}` : ""}</>}
                      {tab === "classes" && <>Class · £{r.price?.toFixed(2)}</>}
                      {tab === "subscriptions" && <>£{r.price?.toFixed(2)} · starts {r.start_date} · {r.address}</>}
                      {tab === "events" && <>{r.event_date} · {r.guests} guests · {r.location}</>}
                    </td>
                    <td className="p-3">
                      <span
                        data-testid={`status-${r.id}`}
                        className={`text-xs uppercase tracking-widest px-2 py-1 rounded ${STATUS_COLORS[status] || "bg-gray-100"}`}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-gray-500">{(r.created_at || "").split("T")[0]}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        {!isComplete && (
                          <button
                            onClick={() => markComplete(r)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`complete-${r.id}`}
                            title="Mark as complete"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                          </button>
                        )}
                        {isComplete && (
                          <button
                            onClick={() => remove(r)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            data-testid={`delete-${r.id}`}
                            title="Delete completed record"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
